import { Group } from "@visx/group";
import { scaleBand, scaleLinear } from "@visx/scale";
import { Bar } from "@visx/shape";

import { formatCompactWholeNumberWithUnit, formatNumberToShort } from "@/web/lib/formatting";
import type { SmbQuestionDetails } from "@/web/routes/_protected/smb/-dashboard/-types";

const CHART_WIDTH = 284;
const CHART_HEIGHT = 256;
const MARGIN = { top: 12, right: 0, bottom: 34, left: 54 };
const TICK_COUNT = 6;
const MAX_VISIBLE_BARS = 3;

type QuestionBarChartProps = {
  chart: NonNullable<SmbQuestionDetails["chart"]>;
};

function niceMax(values: readonly number[]): number {
  const rawMax = Math.max(...values, 0);
  if (rawMax <= 0) return 1;

  const magnitude = 10 ** Math.floor(Math.log10(rawMax));
  const normalized = rawMax / magnitude;
  const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return niceNormalized * magnitude;
}

function labelLines(label: string): string[] {
  const words = label.trim().split(/\s+/);
  if (words.length <= 1) return [label.toUpperCase()];

  const midpoint = Math.ceil(words.length / 2);
  return [
    words.slice(0, midpoint).join(" ").toUpperCase(),
    words.slice(midpoint).join(" ").toUpperCase(),
  ];
}

export function QuestionBarChart({ chart }: QuestionBarChartProps) {
  const rows = chart.data.labels
    .map((label, index) => ({
      label,
      value: chart.data.values[index]?.[0] ?? 0,
    }))
    .slice(0, MAX_VISIBLE_BARS);
  const values = rows.map((row) => row.value);
  const yDomainMax = niceMax(values);
  const xMax = CHART_WIDTH - MARGIN.left - MARGIN.right;
  const yMax = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

  const xScale = scaleBand<string>({
    domain: rows.map((row) => row.label),
    range: [0, xMax],
    paddingInner: 0.62,
    paddingOuter: 0.34,
  });
  const yScale = scaleLinear<number>({
    domain: [0, yDomainMax],
    range: [yMax, 0],
    nice: true,
  });
  const ticks = yScale.ticks(TICK_COUNT);

  return (
    <svg
      role="img"
      aria-label="Details bar chart"
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      className="mt-5 h-auto w-full overflow-visible text-withus-white"
    >
      <Group top={MARGIN.top} left={MARGIN.left}>
        <line x1={0} x2={0} y1={0} y2={yMax} stroke="currentColor" strokeOpacity={0.1} />
        <line x1={0} x2={xMax} y1={yMax} y2={yMax} stroke="currentColor" strokeOpacity={0.1} />

        {ticks.map((tick) => (
          <text
            key={tick}
            x={-8}
            y={yScale(tick)}
            dy="0.35em"
            textAnchor="end"
            className="fill-current text-[0.6875rem] opacity-60"
          >
            {formatCompactWholeNumberWithUnit(
              formatNumberToShort(tick, { showThousands: true }),
              chart.unit.tag,
            )}
          </text>
        ))}

        {rows.map((row) => {
          const barX = xScale(row.label) ?? 0;
          const barWidth = xScale.bandwidth();
          const barY = yScale(row.value);
          const barHeight = yMax - barY;
          const labelY = Math.max(0, barY - 22);
          const formattedValue = formatCompactWholeNumberWithUnit(
            formatNumberToShort(row.value, { showThousands: true }),
            chart.unit.tag,
          );
          const valueLabelWidth = Math.max(48, formattedValue.length * 7 + 10);
          const centerX = barX + barWidth / 2;

          return (
            <Group key={row.label}>
              <Bar x={barX} y={barY} width={barWidth} height={barHeight} fill="var(--withus-red)" />
              <rect
                x={centerX - valueLabelWidth / 2}
                y={labelY}
                width={valueLabelWidth}
                height={18}
                fill="var(--withus-white)"
                stroke="var(--withus-red)"
              />
              <text
                x={centerX}
                y={labelY + 13}
                textAnchor="middle"
                className="fill-withus-red text-[0.75rem] font-semibold"
              >
                {formattedValue}
              </text>
              {labelLines(row.label).map((line, lineIndex) => (
                <text
                  key={`${row.label}-${line}`}
                  x={centerX}
                  y={yMax + 16 + lineIndex * 10}
                  textAnchor="middle"
                  className="fill-current text-[0.625rem] font-semibold tracking-[0.05em] opacity-60"
                >
                  {line}
                </text>
              ))}
            </Group>
          );
        })}
      </Group>
    </svg>
  );
}
